/*
 This file is part of GNU Anastasis
 (C) 2021-2022 Anastasis SARL

 GNU Anastasis is free software; you can redistribute it and/or modify it under the
 terms of the GNU Affero General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Anastasis is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU Affero General Public License for more details.

 You should have received a copy of the GNU Affero General Public License along with
 GNU Anastasis; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */

import { Codec, codecForString } from "@gnu-taler/taler-util";
import { useCallback, useEffect, useState } from "preact/hooks";
import { localStorageMap } from "../utils/observable.js";

declare const opaque_StorageKey: unique symbol;

export type StorageKey<Key> = {
  id: string;
  [opaque_StorageKey]: true;
  codec: Codec<Key>;
};

export function buildStorageKey<Key>(
  name: string,
  codec: Codec<Key>,
): StorageKey<Key>;
export function buildStorageKey(name: string): StorageKey<string>;
export function buildStorageKey<Key = string>(
  name: string,
  codec?: Codec<Key>,
): StorageKey<Key> {
  return {
    id: name,
    codec: codec ?? (codecForString() as unknown as Codec<Key>),
  } as StorageKey<Key>;
}

export interface StorageState<Type = string> {
  value?: Type;
  update: (s: Type) => void;
  reset: () => void;
}

const storage = localStorageMap();
//with initial value
export function useLocalStorage<Type = string>(
  key: StorageKey<Type>,
  defaultValue: Type,
): Required<StorageState<Type>>;
//without initial value
export function useLocalStorage<Type = string>(
  key: StorageKey<Type>,
): StorageState<Type>;
// impl
export function useLocalStorage<Type = string>(
  key: StorageKey<Type>,
  defaultValue?: Type,
): StorageState<Type> {
  const renderedValue = storage.get(key.id);
  const current = convert(renderedValue, key, defaultValue);

  const [, setVersion] = useState(0);

  useEffect(() => {
    const unsubscribe = storage.onUpdate(key.id, () => {
      setVersion((version) => version + 1);
    });
    // The value can change after render but before this effect subscribes.
    // Re-read it after subscribing so that such an update is not lost.
    if (storage.get(key.id) !== renderedValue) {
      setVersion((version) => version + 1);
    }
    return unsubscribe;
  }, [key.id, renderedValue]);

  const setValue = useCallback(
    (value?: Type): void => {
      if (value === undefined) {
        storage.delete(key.id);
      } else {
        storage.set(key.id, JSON.stringify(value));
      }
    },
    [key.id],
  );
  const reset = useCallback(() => {
    setValue(defaultValue);
  }, [defaultValue, setValue]);

  return {
    value: current,
    update: setValue,
    reset,
  };
}

function convert<Type>(
  updated: string | undefined,
  key: StorageKey<Type>,
  defaultValue?: Type,
): Type | undefined {
  if (updated === undefined) return defaultValue; //optional
  try {
    return key.codec.decode(JSON.parse(updated));
  } catch (e) {
    console.error("Decoding error", e);
    //decode error
    return defaultValue;
  }
}
